import { useId, useState } from "react"
import { ExternalLink, Loader2, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link, useBlocker } from "react-router"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
  Textarea,
} from "@f2e/ui/components/base"
import {
  useUpdateClientUserApi,
  type ClientUserDetail,
  type UpdateClientUserRequest,
} from "@/hooks/api/crm"
import { useBeforeUnload } from "@/hooks/useBeforeUnload"
import { normalizeApiError } from "@/lib/apiErrors"
import { validateUserFields } from "@/lib/userValidation"

const fields = [
  "provider_notes",
  "alias",
  "email",
  "phone",
  "company",
  "job_title",
] as const
type CrmDraft = Record<(typeof fields)[number], string>
const toDraft = (user: ClientUserDetail): CrmDraft =>
  Object.fromEntries(
    fields.map((field) => [field, user[field] || ""])
  ) as CrmDraft

export interface ThreadCrmEditorProps {
  organizationId: string
  workspaceId: string
  user: ClientUserDetail
  onSaved: (user: ClientUserDetail) => void
  onClose: () => void
}

export const ThreadCrmEditor = ({
  organizationId,
  workspaceId,
  user,
  onSaved,
  onClose,
}: ThreadCrmEditorProps) => {
  const { t } = useTranslation()
  const id = useId()
  const [baseline, setBaseline] = useState(() => toDraft(user))
  const [draft, setDraft] = useState(baseline)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  const { fetchFn: updateUser } = useUpdateClientUserApi(
    organizationId,
    workspaceId,
    user.id
  )
  const changes: UpdateClientUserRequest = Object.fromEntries(
    fields
      .filter((field) => draft[field] !== baseline[field])
      .map((field) => [field, draft[field]])
  )
  const dirty = Object.keys(changes).length > 0
  const validation = validateUserFields(draft)
  const blocker = useBlocker(dirty || saving)
  useBeforeUnload(dirty || saving)

  const close = () => {
    if (saving) return
    if (dirty) setConfirmClose(true)
    else onClose()
  }
  const save = async () => {
    if (
      saving ||
      !dirty ||
      !validation.isEmailValid ||
      !validation.isPhoneValid
    )
      return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const response = await updateUser({ jsonBody: changes, retry: 0 })
      if (!response?.data) throw new Error("CRM customer response unavailable")
      const next = toDraft(response.data)
      setBaseline(next)
      setDraft(next)
      setSaved(true)
      onSaved(response.data)
    } catch (error) {
      const normalized = normalizeApiError(error)
      setError(
        normalized.messageKey
          ? t(normalized.messageKey)
          : t("historyList.thread.crmNotes.saveFailed")
      )
    } finally {
      setSaving(false)
    }
  }
  const input = (
    field: Exclude<(typeof fields)[number], "provider_notes">,
    label: string,
    invalid = false
  ) => (
    <div className="space-y-1.5">
      <Label htmlFor={`${id}-${field}`} className="text-xs">
        {t(label)}
      </Label>
      <Input
        id={`${id}-${field}`}
        type={field === "email" ? "email" : field === "phone" ? "tel" : "text"}
        value={draft[field]}
        maxLength={field === "alias" ? 256 : undefined}
        disabled={saving}
        aria-invalid={invalid}
        aria-describedby={invalid ? `${id}-${field}-error` : undefined}
        onChange={(event) => {
          setDraft({ ...draft, [field]: event.target.value })
          setSaved(false)
        }}
        className="h-9"
      />
      {invalid && (
        <p id={`${id}-${field}-error`} className="text-xs text-destructive">
          {t(
            `crm.userManagement.addUser.${field === "email" ? "emailInvalid" : "phoneInvalid"}`
          )}
        </p>
      )}
    </div>
  )

  return (
    <>
      <form
        aria-labelledby={`${id}-title`}
        onSubmit={(event) => {
          event.preventDefault()
          void save()
        }}
        className="flex h-full min-h-0 flex-col"
      >
        <div className="flex shrink-0 items-start justify-between gap-2 border-b px-4 py-3">
          <div className="min-w-0">
            <h3 id={`${id}-title`} className="text-sm font-semibold">
              {t("historyList.thread.crmNotes.customerDetails")}
            </h3>
            <p
              className="mt-1 truncate text-xs text-muted-foreground"
              title={user.display_name || undefined}
            >
              {user.display_name}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            disabled={saving}
            onClick={close}
            aria-label={t("historyList.thread.crmNotes.close")}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain p-4">
          <div className="space-y-2">
            <Label htmlFor={`${id}-notes`} className="text-xs">
              {t("historyList.thread.crmNotes.title")}
            </Label>
            <Textarea
              id={`${id}-notes`}
              autoFocus
              value={draft.provider_notes}
              disabled={saving}
              onChange={(event) => {
                setDraft({ ...draft, provider_notes: event.target.value })
                setSaved(false)
              }}
              className="field-sizing-fixed h-40 min-h-36 max-h-64 resize-y text-sm leading-6"
            />
          </div>
          <div className="space-y-4 border-t pt-4">
            <h4 className="text-xs font-medium text-muted-foreground">
              {t("crm.userManagement.detail.basicInfo")}
            </h4>
            {input("alias", "crm.userManagement.detail.alias")}
            {input(
              "email",
              "crm.userManagement.detail.email",
              !validation.isEmailValid
            )}
            {input(
              "phone",
              "crm.userManagement.addUser.phone",
              !validation.isPhoneValid
            )}
            <details className="group">
              <summary className="cursor-pointer rounded-sm text-xs font-medium text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {t("historyList.thread.crmNotes.moreDetails")}
              </summary>
              <div className="mt-4 space-y-4">
                {input("company", "crm.userManagement.addUser.company")}
                {input("job_title", "crm.userManagement.addUser.jobTitle")}
              </div>
            </details>
          </div>
          <Link
            to={`/workspaces/${workspaceId}/users/${user.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-4"
          >
            {t("historyList.thread.crmNotes.fullProfile")}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </Link>
        </div>
        <div className="shrink-0 space-y-2 border-t bg-background p-4">
          {error && (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          )}
          <p role="status" className="text-xs text-muted-foreground">
            {t(
              saving
                ? "historyList.thread.crmNotes.saving"
                : saved
                  ? "historyList.thread.crmNotes.saved"
                  : dirty
                    ? "historyList.thread.crmNotes.unsaved"
                    : "historyList.thread.crmNotes.saveHint"
            )}
          </p>
          <div className="flex justify-end gap-2 pr-14">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={saving}
              onClick={close}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={
                !dirty ||
                saving ||
                !validation.isEmailValid ||
                !validation.isPhoneValid
              }
            >
              {saving && (
                <Loader2
                  className="mr-1.5 h-3.5 w-3.5 animate-spin"
                  aria-hidden="true"
                />
              )}
              {t("common.save")}
            </Button>
          </div>
        </div>
      </form>
      <AlertDialog
        open={confirmClose || blocker.state === "blocked"}
        onOpenChange={(open) => {
          if (!open && !saving) {
            setConfirmClose(false)
            if (blocker.state === "blocked") blocker.reset()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("historyList.thread.crmNotes.discardTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("historyList.thread.crmNotes.discardDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={saving}
              onClick={() => {
                setConfirmClose(false)
                if (blocker.state === "blocked") blocker.reset()
              }}
            >
              {t("historyList.thread.crmNotes.keepEditing")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              onClick={(event) => {
                // Confirming must not also reset a pending browser navigation.
                event.preventDefault()
                setConfirmClose(false)
                setDraft(baseline)
                setError(null)
                setSaved(false)
                if (blocker.state === "blocked") blocker.proceed()
                else onClose()
              }}
            >
              {t("historyList.thread.crmNotes.discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
